import { useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, Database } from "lucide-react";

import { portalFetch } from "@/lib/portalApi";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePermissions } from "@/hooks/usePermissions";
import { OdbcFieldError } from "@/components/portal/shared/OdbcFieldError";
import {
  hasOdbcFieldError,
  odbcFieldErrors,
  odbcInputProps,
} from "@/lib/odbcConnectionFields";

export type DatabaseConnectionValues = {
  host: string;
  database: string;
  username: string;
  password: string;
};

type DatabaseConnectionTestProps = {
  values: DatabaseConnectionValues;
  onChange: (field: keyof DatabaseConnectionValues, value: string) => void;
  onConnectionTested: (result: {
    success: boolean;
    connectionString: string | null;
    existingSchemas: string[];
  }) => void;
  disabled?: boolean;
  connectionTested?: boolean;
  existingSchemas?: string[];
};

export function DatabaseConnectionTest({
  values,
  onChange,
  onConnectionTested,
  disabled,
  connectionTested,
}: DatabaseConnectionTestProps) {
  const { token } = useAuth();
  // Test Connection dials a host the user types: org admin and above only (dp #2438 / #2439).
  const { bypassesGroups: canProbeConnection } = usePermissions();
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState(false);

  // No `type` is sent, so the server treats it as SQL Server and refuses `;`
  // `{` `}` in any of the four (`@/lib/odbcConnectionFields`).
  const odbcErrors = odbcFieldErrors(values);
  const canTest =
    values.host.trim() !== "" &&
    values.database.trim() !== "" &&
    values.username.trim() !== "" &&
    values.password.trim() !== "" &&
    !hasOdbcFieldError(odbcErrors);

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);
    setTestSuccess(false);

    try {
      const result = await portalFetch<{
        success: boolean;
        errorMessage?: string;
        existingSchemas?: string[];
      }>("/api/v1/gateway/business2cloud/api/v1/database/test-connection", {
        method: "POST",
        token: token ?? undefined,
        body: {
          host: values.host.trim(),
          database: values.database.trim(),
          username: values.username.trim(),
          password: values.password.trim(),
        },
      });

      if (result.success) {
        setTestSuccess(true);
        const connectionString = `Data Source=${values.host};Initial Catalog=${values.database};User Id=${values.username};Password=${values.password}`;
        onConnectionTested({
          success: true,
          connectionString,
          existingSchemas: result.existingSchemas ?? [],
        });
      } else {
        setError(result.errorMessage || "Connection test failed");
        onConnectionTested({
          success: false,
          connectionString: null,
          existingSchemas: [],
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Connection test failed";
      setError(message);
      onConnectionTested({
        success: false,
        connectionString: null,
        existingSchemas: [],
      });
    } finally {
      setTesting(false);
    }
  };

  // Reset success state when connection values change
  const handleFieldChange = (
    field: keyof DatabaseConnectionValues,
    value: string,
  ) => {
    onChange(field, value);
    if (testSuccess || connectionTested) {
      setTestSuccess(false);
      setError(null);
      // Notify parent that connection needs to be re-tested
      onConnectionTested({
        success: false,
        connectionString: null,
        existingSchemas: [],
      });
    }
  };

  const showSuccess = testSuccess || connectionTested;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="db-host">Host / Server</Label>
        <Input
          id="db-host"
          placeholder="e.g., server.database.windows.net"
          value={values.host}
          onChange={(e) => handleFieldChange("host", e.target.value)}
          disabled={disabled || testing}
          {...odbcInputProps("db-host-error", odbcErrors.host)}
        />
        <OdbcFieldError id="db-host-error" message={odbcErrors.host} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="db-database">Database</Label>
        <Input
          id="db-database"
          placeholder="e.g., ClientDB"
          value={values.database}
          onChange={(e) => handleFieldChange("database", e.target.value)}
          disabled={disabled || testing}
          {...odbcInputProps("db-database-error", odbcErrors.database)}
        />
        <OdbcFieldError id="db-database-error" message={odbcErrors.database} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="db-username">Username</Label>
          <Input
            id="db-username"
            placeholder="Username"
            value={values.username}
            onChange={(e) => handleFieldChange("username", e.target.value)}
            disabled={disabled || testing}
            {...odbcInputProps("db-username-error", odbcErrors.username)}
          />
          <OdbcFieldError
            id="db-username-error"
            message={odbcErrors.username}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="db-password">Password</Label>
          <Input
            id="db-password"
            type="password"
            placeholder="Password"
            value={values.password}
            onChange={(e) => handleFieldChange("password", e.target.value)}
            disabled={disabled || testing}
            autoComplete="off"
            {...odbcInputProps("db-password-error", odbcErrors.password)}
          />
          <OdbcFieldError
            id="db-password-error"
            message={odbcErrors.password}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        {canProbeConnection && (
          <Button
            type="button"
            variant="outline"
            onClick={handleTestConnection}
            disabled={!canTest || disabled || testing}
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Database className="mr-2 h-4 w-4" />
                Test Connection
              </>
            )}
          </Button>
        )}

        {showSuccess && (
          <div className="flex items-center gap-2 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />
            Connection successful
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
    </div>
  );
}
